import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { BigBanner as BigBannerProps } from 'store/landing/types';
import isDesktop from 'store/app/selector/isDesktop';
import ScrollImage from 'assets/images/scroll.png';
import { useLandingPageContext } from 'context/LandingPageContext';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import getLandingPageName from 'store/landing/selector/getLandingPageName';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmLandingCTAEvent from 'utils/gtm/gtmLandingCTAEvent';
import StudioSelectionSearchBar from 'components/Common/StudioSelectionSearchBar/StudioSelectionSearchBar';
import LPButton, { BUTTON_TYPE } from '../LPButton/LPButton';
import MessageCardGroupNativeButtons from '../MessageCardGroup/MessageCardGroupNativeButtons/MessageCardGroupNativeButtons';
import styles from './BigBanner.module.scss';

const BigBanner: FC<BigBannerProps> = ({
  bannerImageUrl,
  bannerImageUrlDesktop,
  bannerImageUrlMobile,
  features,
  headingTag = 'h2',
  h1,
  h1Color,
  h1FontWeight,
  h2,
  h2Color,
  h2FontWeight,
  reference,
  height,
  entryId,
  hideScrollIcon,
}) => {
  if (!bannerImageUrl) {
    return null;
  }

  const isDesktopDevice = useSelector(isDesktop);
  const imageUrl = isDesktopDevice ? bannerImageUrlDesktop : bannerImageUrlMobile;
  const { isSearchBarEnabled, categoryId } = useLandingPageContext();
  const isSearchBarShown =
    isSearchBarEnabled && (features?.includes('studio-selection-search-bar') || entryId === '3sLDlcSNFEyiPl43YNhm7c');
  const backgroundImage = `url(${imageUrl})`;
  const locale = useSelector(getLocaleSelector);
  const landingPageName = useSelector(getLandingPageName);

  const trackLandingPageCTA = (buttonText: string) => {
    const funnelStep = landingPageName === 'Home' ? 'homepage' : 'landing';
    gtmSendEvent(gtmLandingCTAEvent(buttonText, locale, funnelStep));
  };

  return (
    <div className={styles.container}>
      <div className={styles.image} style={{ backgroundImage, height }} />
      <div className={styles.captionsBox}>
        {React.createElement(headingTag, {
          className: styles.h1,
          style: { color: h1Color, fontWeight: h1FontWeight },
          dangerouslySetInnerHTML: { __html: h1 },
        })}
        {h2 && (
          <p
            className={styles.subheading}
            dangerouslySetInnerHTML={{ __html: h2 }}
            style={{ color: h2Color, fontWeight: h2FontWeight }}
          />
        )}

        {isSearchBarShown ? (
          <StudioSelectionSearchBar categoryId={categoryId} className={styles.searchBar} />
        ) : (
          reference && (
            <>
              {reference.contentType === 'button' && reference.button.label && (
                <LPButton
                  buttonColor={BUTTON_TYPE[reference.button.style].textColor}
                  buttonBackgroundColor={BUTTON_TYPE[reference.button.style].backgroundColor}
                  buttonBorderColor={BUTTON_TYPE[reference.button.style].borderColor}
                  referenceUrl={reference.button.referenceUrl}
                >
                  <span onClick={() => trackLandingPageCTA(reference.button.label)} className={styles.buttonText}>
                    {reference.button.label}
                  </span>
                </LPButton>
              )}
              {reference.contentType === 'buttonNativeAppDownloadFromStore' && reference.buttonNativeApp.stores && (
                <MessageCardGroupNativeButtons buttonNativeApp={reference.buttonNativeApp} />
              )}
            </>
          )
        )}

        {!hideScrollIcon && <img alt="scroll" src={ScrollImage} className={styles.scrollIcon} />}
      </div>
    </div>
  );
};

export default BigBanner;
